import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowLeftIcon,
  PrinterIcon,
  CreditCardIcon,
  BanknotesIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { getInvoiceById, Invoice, formatCurrency, getStatusColor, getTypeLabel, recordPayment } from '../api/invoiceApi'
import { paymentConfigApi } from '../api/paymentConfigApi'
import { useAuth } from '../hooks/useAuth'
import { usePaymentWebSocket, PaymentStatus } from '../hooks/usePaymentWebSocket'

// Payment method type
type PaymentMethodType = 'MPESA' | 'CASH'

export default function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  // Determine if user is parent or admin
  const isParent = user?.role?.toLowerCase() === 'parent'
  const isSchoolAdmin = user?.role?.toLowerCase() === 'school_admin' || user?.role?.toLowerCase() === 'super_admin'

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType | null>(null)
  const [paymentAmount, setPaymentAmount] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [cashReference, setCashReference] = useState('')
  const [isProcessing, setIsProcessing] = useState(false)
  const [showPaymentStatus, setShowPaymentStatus] = useState(false)
  const [paymentRef, setPaymentRef] = useState<string | null>(null)

  // WebSocket hook for real-time payment status
  const {
    status: wsStatus,
    message: wsMessage,
    isConnected,
    connect: connectWebSocket,
    disconnect: disconnectWebSocket,
    notification,
  } = usePaymentWebSocket({
    onComplete: (notif) => {
      setIsProcessing(false)
      toast.success(notif.message || 'Payment completed successfully!')
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      // Auto close after showing success
      setTimeout(() => {
        setShowPaymentStatus(false)
        setShowPaymentModal(false)
        setPaymentRef(null)
      }, 3000)
    },
    onError: (notif) => {
      setIsProcessing(false)
      toast.error(notif.message || 'Payment failed')
    },
  })

  // Fetch invoice data
  const { data, isLoading, error } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => getInvoiceById(id!),
    enabled: !!id,
  })

  const invoice: Invoice | undefined = data?.data

  // Record cash payment mutation
  const recordPaymentMutation = useMutation({
    mutationFn: (paymentData: { amount: number; paymentMethod: string; reference: string }) =>
      recordPayment(id!, paymentData),
    onSuccess: () => {
      toast.success('Payment recorded successfully')
      queryClient.invalidateQueries({ queryKey: ['invoice', id] })
      closePaymentModal()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to record payment')
    },
  })

  // STK Push mutation
  const stkPushMutation = useMutation({
    mutationFn: (data: { amount: number; phoneNumber: string; orderId: string; type: 'invoice' }) =>
      paymentConfigApi.initiateStkPush(data),
    onSuccess: (response) => {
      const ref = response.data.paymentRef
      setPaymentRef(ref)
      setShowPaymentStatus(true)
      setShowPaymentModal(false)
      setIsProcessing(false) // Reset processing state when moving to status overlay
      // Connect to WebSocket for real-time status updates
      connectWebSocket(ref)
      toast.success(response.message || 'Payment request sent! Check your phone.')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to initiate payment')
      setIsProcessing(false)
    },
  })

  const closePaymentModal = () => {
    setShowPaymentModal(false)
    setPaymentMethod(null)
    setPaymentAmount('')
    setPhoneNumber('')
    setCashReference('')
    setIsProcessing(false)
  }

  const closePaymentStatus = () => {
    disconnectWebSocket()
    setShowPaymentStatus(false)
    setPaymentRef(null)
    setIsProcessing(false)
  }

  // Get status display info
  const getStatusDisplay = (status: PaymentStatus) => {
    switch (status) {
      case 'PENDING':
        return {
          icon: <ClockIcon className="h-16 w-16 text-yellow-500" />,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-50',
          spinnerColor: 'border-yellow-500',
        }
      case 'PROCESSING':
        return {
          icon: <DevicePhoneMobileIcon className="h-16 w-16 text-blue-500" />,
          color: 'text-blue-600',
          bgColor: 'bg-blue-50',
          spinnerColor: 'border-blue-500',
        }
      case 'COMPLETED':
        return {
          icon: <CheckCircleIcon className="h-16 w-16 text-green-500" />,
          color: 'text-green-600',
          bgColor: 'bg-green-50',
          spinnerColor: 'border-green-500',
        }
      case 'FAILED':
      case 'CANCELLED':
        return {
          icon: <XCircleIcon className="h-16 w-16 text-red-500" />,
          color: 'text-red-600',
          bgColor: 'bg-red-50',
          spinnerColor: 'border-red-500',
        }
      case 'TIMEOUT':
        return {
          icon: <ExclamationTriangleIcon className="h-16 w-16 text-orange-500" />,
          color: 'text-orange-600',
          bgColor: 'bg-orange-50',
          spinnerColor: 'border-orange-500',
        }
      default:
        return {
          icon: <ClockIcon className="h-16 w-16 text-gray-500" />,
          color: 'text-gray-600',
          bgColor: 'bg-gray-50',
          spinnerColor: 'border-gray-500',
        }
    }
  }

  const handlePaymentSubmit = async () => {
    if (!invoice) return

    const amount = parseFloat(paymentAmount)
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount')
      return
    }

    if (amount > invoice.balanceDue) {
      toast.error(`Amount cannot exceed balance due (${formatCurrency(invoice.balanceDue, invoice.currency)})`)
      return
    }

    setIsProcessing(true)

    if (paymentMethod === 'MPESA') {
      // Validate phone number
      if (!phoneNumber || phoneNumber.length < 9) {
        toast.error('Please enter a valid phone number')
        setIsProcessing(false)
        return
      }

      // Format phone number (remove leading 0 or +254)
      let formattedPhone = phoneNumber.replace(/\s/g, '')
      if (formattedPhone.startsWith('+254')) {
        formattedPhone = formattedPhone.substring(1)
      } else if (formattedPhone.startsWith('0')) {
        formattedPhone = '254' + formattedPhone.substring(1)
      } else if (!formattedPhone.startsWith('254')) {
        formattedPhone = '254' + formattedPhone
      }

      stkPushMutation.mutate({
        amount,
        phoneNumber: formattedPhone,
        orderId: invoice._id,
        type: 'invoice',
      })
    } else if (paymentMethod === 'CASH') {
      if (!cashReference) {
        toast.error('Please enter a reference number')
        setIsProcessing(false)
        return
      }

      recordPaymentMutation.mutate({
        amount,
        paymentMethod: 'CASH',
        reference: cashReference,
      })
    }
  }

  const getStatusBadgeClass = (status: string) => {
    const color = getStatusColor(status as any)
    const classes: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      blue: 'bg-blue-100 text-blue-800',
      orange: 'bg-orange-100 text-orange-800',
      green: 'bg-green-100 text-green-800',
      red: 'bg-red-100 text-red-800',
      purple: 'bg-purple-100 text-purple-800',
    }
    return classes[color] || classes.gray
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    )
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <XCircleIcon className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">Invoice not found</h3>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  const canPay = invoice.status !== 'PAID' && invoice.status !== 'CANCELLED' && invoice.balanceDue > 0

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back
        </button>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => window.print()}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <PrinterIcon className="h-4 w-4 mr-2" />
            Print
          </button>
          {canPay && (
            <button
              onClick={() => setShowPaymentModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              <CreditCardIcon className="h-4 w-4 mr-2" />
              Pay Now
            </button>
          )}
        </div>
      </div>

      {/* Invoice Card */}
      <div className="bg-white shadow-lg rounded-lg overflow-hidden print:shadow-none">
        {/* Invoice Header */}
        <div className="px-6 py-8 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">INVOICE</h1>
              <p className="text-lg font-semibold text-indigo-600 mt-1">{invoice.invoiceNumber}</p>
            </div>
            <div className="text-right">
              <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(invoice.status)}`}>
                {invoice.status.replace('_', ' ')}
              </span>
              <p className="text-sm text-gray-500 mt-2">
                {getTypeLabel(invoice.type)}
              </p>
            </div>
          </div>
        </div>

        {/* Invoice Details */}
        <div className="px-6 py-6">
          <div className="grid grid-cols-2 gap-8 mb-8">
            {/* Bill To */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-2">Bill To</h3>
              <p className="font-semibold text-gray-900">{invoice.customerName}</p>
              {invoice.customerEmail && <p className="text-sm text-gray-600">{invoice.customerEmail}</p>}
              {invoice.customerPhone && <p className="text-sm text-gray-600">{invoice.customerPhone}</p>}
              {invoice.customerAddress && <p className="text-sm text-gray-600">{invoice.customerAddress}</p>}
              {invoice.studentName && (
                <p className="text-sm text-gray-500 mt-1">Student: {invoice.studentName}</p>
              )}
            </div>

            {/* Invoice Info */}
            <div className="text-right">
              <div className="space-y-2">
                <div>
                  <span className="text-sm text-gray-500">Issue Date:</span>
                  <span className="ml-2 text-sm font-medium text-gray-900">
                    {new Date(invoice.issueDate).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-gray-500">Due Date:</span>
                  <span className={`ml-2 text-sm font-medium ${
                    new Date(invoice.dueDate) < new Date() && invoice.status !== 'PAID'
                      ? 'text-red-600'
                      : 'text-gray-900'
                  }`}>
                    {new Date(invoice.dueDate).toLocaleDateString()}
                  </span>
                </div>
                {invoice.termName && (
                  <div>
                    <span className="text-sm text-gray-500">Term:</span>
                    <span className="ml-2 text-sm font-medium text-gray-900">{invoice.termName}</span>
                  </div>
                )}
                {invoice.academicYear && (
                  <div>
                    <span className="text-sm text-gray-500">Year:</span>
                    <span className="ml-2 text-sm font-medium text-gray-900">{invoice.academicYear}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900">{invoice.title}</h2>
            {invoice.description && <p className="text-sm text-gray-600 mt-1">{invoice.description}</p>}
          </div>

          {/* Line Items */}
          <div className="border rounded-lg overflow-hidden mb-6">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Discount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Tax</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoice.items.map((item, index) => (
                  <tr key={item._id || index}>
                    <td className="px-4 py-3 text-sm text-gray-900">{item.description}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.quantity}</td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {formatCurrency(item.unitPrice, invoice.currency)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">
                      {item.discountType === 'PERCENTAGE' ? `${item.discount}%` : formatCurrency(item.discount, invoice.currency)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 text-right">{item.taxRate}%</td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">
                      {formatCurrency(item.total, invoice.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="flex justify-end">
            <div className="w-64 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal:</span>
                <span className="text-gray-900">{formatCurrency(invoice.subtotal, invoice.currency)}</span>
              </div>
              {invoice.totalDiscount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Discount:</span>
                  <span className="text-green-600">-{formatCurrency(invoice.totalDiscount, invoice.currency)}</span>
                </div>
              )}
              {invoice.totalTax > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Tax:</span>
                  <span className="text-gray-900">{formatCurrency(invoice.totalTax, invoice.currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-semibold border-t pt-2">
                <span className="text-gray-900">Grand Total:</span>
                <span className="text-gray-900">{formatCurrency(invoice.grandTotal, invoice.currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Paid:</span>
                <span className="text-green-600">{formatCurrency(invoice.paidAmount, invoice.currency)}</span>
              </div>
              <div className="flex justify-between text-base font-semibold border-t pt-2">
                <span className="text-gray-900">Balance Due:</span>
                <span className={invoice.balanceDue > 0 ? 'text-red-600' : 'text-green-600'}>
                  {formatCurrency(invoice.balanceDue, invoice.currency)}
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {invoice.notes && (
            <div className="mt-8 p-4 bg-gray-50 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Notes</h4>
              <p className="text-sm text-gray-600">{invoice.notes}</p>
            </div>
          )}

          {/* Payment History */}
          {invoice.payments && invoice.payments.length > 0 && (
            <div className="mt-8">
              <h4 className="text-sm font-medium text-gray-700 mb-4">Payment History</h4>
              <div className="border rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Method</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {invoice.payments.map((payment, index) => (
                      <tr key={payment._id || index}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {new Date(payment.paidAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{payment.paymentMethod}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{payment.reference}</td>
                        <td className="px-4 py-3 text-sm font-medium text-green-600 text-right">
                          {formatCurrency(payment.amount, invoice.currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {paymentMethod ? `Pay with ${paymentMethod}` : 'Select Payment Method'}
              </h3>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-4">
              {!paymentMethod ? (
                // Payment Method Selection
                <div className="space-y-3">
                  <p className="text-sm text-gray-600 mb-4">
                    Balance Due: <span className="font-semibold text-red-600">{formatCurrency(invoice.balanceDue, invoice.currency)}</span>
                  </p>

                  {/* M-Pesa Option - Available for all */}
                  <button
                    onClick={() => {
                      setPaymentMethod('MPESA')
                      setPaymentAmount(invoice.balanceDue.toString())
                    }}
                    className="w-full flex items-center p-4 border-2 border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
                  >
                    <div className="flex-shrink-0 h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                      <DevicePhoneMobileIcon className="h-6 w-6 text-green-600" />
                    </div>
                    <div className="ml-4 text-left">
                      <p className="font-medium text-gray-900">M-Pesa</p>
                      <p className="text-sm text-gray-500">Pay using M-Pesa mobile money</p>
                    </div>
                  </button>

                  {/* Cash Option - Only for School Admin */}
                  {isSchoolAdmin && (
                    <button
                      onClick={() => {
                        setPaymentMethod('CASH')
                        setPaymentAmount(invoice.balanceDue.toString())
                      }}
                      className="w-full flex items-center p-4 border-2 border-gray-200 rounded-lg hover:border-indigo-500 hover:bg-indigo-50 transition-colors"
                    >
                      <div className="flex-shrink-0 h-12 w-12 bg-indigo-100 rounded-full flex items-center justify-center">
                        <BanknotesIcon className="h-6 w-6 text-indigo-600" />
                      </div>
                      <div className="ml-4 text-left">
                        <p className="font-medium text-gray-900">Cash</p>
                        <p className="text-sm text-gray-500">Record cash payment</p>
                      </div>
                    </button>
                  )}
                </div>
              ) : paymentMethod === 'MPESA' ? (
                // M-Pesa Payment Form
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{invoice.currency}</span>
                      <input
                        type="number"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        placeholder="0.00"
                        max={invoice.balanceDue}
                        className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Max: {formatCurrency(invoice.balanceDue, invoice.currency)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">+254</span>
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                        placeholder="7XXXXXXXX"
                        maxLength={10}
                        className="w-full pl-14 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">Enter Safaricom number (e.g., 0712345678)</p>
                  </div>

                  <div className="flex items-start space-x-2 p-3 bg-yellow-50 rounded-lg">
                    <ClockIcon className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-yellow-800">
                      You will receive an M-Pesa prompt on your phone. Enter your PIN to complete the payment.
                    </p>
                  </div>
                </div>
              ) : (
                // Cash Payment Form
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Amount</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">{invoice.currency}</span>
                      <input
                        type="number"
                        value={paymentAmount}
                        onChange={(e) => setPaymentAmount(e.target.value)}
                        placeholder="0.00"
                        max={invoice.balanceDue}
                        className="w-full pl-12 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Max: {formatCurrency(invoice.balanceDue, invoice.currency)}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
                    <input
                      type="text"
                      value={cashReference}
                      onChange={(e) => setCashReference(e.target.value)}
                      placeholder="Receipt or reference number"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={paymentMethod ? () => setPaymentMethod(null) : closePaymentModal}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                {paymentMethod ? 'Back' : 'Cancel'}
              </button>
              {paymentMethod && (
                <button
                  onClick={handlePaymentSubmit}
                  disabled={isProcessing}
                  className={`px-4 py-2 rounded-lg text-white ${
                    paymentMethod === 'MPESA'
                      ? 'bg-green-600 hover:bg-green-700'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {isProcessing ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    `Pay ${paymentAmount ? formatCurrency(parseFloat(paymentAmount), invoice.currency) : ''}`
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Payment Status Overlay */}
      {showPaymentStatus && paymentRef && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full shadow-2xl overflow-hidden">
            {/* Status Header */}
            <div className={`px-6 py-4 ${getStatusDisplay(wsStatus).bgColor}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Payment Status</h3>
                {isConnected && (
                  <span className="flex items-center text-xs text-green-600">
                    <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                    Live
                  </span>
                )}
              </div>
            </div>

            {/* Status Body */}
            <div className="px-6 py-8">
              <div className="text-center">
                {/* Spinner for pending/processing states */}
                {(wsStatus === 'PENDING' || wsStatus === 'PROCESSING' || wsStatus === 'IDLE') && (
                  <div className="relative mx-auto w-20 h-20 mb-6">
                    <div className={`absolute inset-0 border-4 ${getStatusDisplay(wsStatus).spinnerColor} border-t-transparent rounded-full animate-spin`}></div>
                    <div className="absolute inset-2 flex items-center justify-center">
                      {wsStatus === 'PROCESSING' ? (
                        <DevicePhoneMobileIcon className="h-8 w-8 text-blue-500" />
                      ) : (
                        <ClockIcon className="h-8 w-8 text-yellow-500" />
                      )}
                    </div>
                  </div>
                )}

                {/* Icon for final states */}
                {(wsStatus === 'COMPLETED' || wsStatus === 'FAILED' || wsStatus === 'CANCELLED' || wsStatus === 'TIMEOUT') && (
                  <div className="mb-6">
                    {getStatusDisplay(wsStatus).icon}
                  </div>
                )}

                {/* Status Text */}
                <h4 className={`text-xl font-semibold mb-2 ${getStatusDisplay(wsStatus).color}`}>
                  {wsStatus === 'IDLE' && 'Connecting...'}
                  {wsStatus === 'PENDING' && 'Waiting for Payment'}
                  {wsStatus === 'PROCESSING' && 'Check Your Phone'}
                  {wsStatus === 'COMPLETED' && 'Payment Successful!'}
                  {wsStatus === 'FAILED' && 'Payment Failed'}
                  {wsStatus === 'CANCELLED' && 'Payment Cancelled'}
                  {wsStatus === 'TIMEOUT' && 'Payment Timed Out'}
                </h4>

                <p className="text-sm text-gray-600 mb-4">
                  {wsMessage || 'Please wait while we process your payment...'}
                </p>

                {/* Transaction details for completed payments */}
                {wsStatus === 'COMPLETED' && notification && (
                  <div className="bg-green-50 rounded-lg p-4 mt-4 text-left">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Amount Paid:</span>
                      <span className="font-semibold text-green-600">
                        {notification.amount ? formatCurrency(notification.amount, invoice?.currency || 'KES') : '-'}
                      </span>
                    </div>
                    {notification.transactionRef && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">M-Pesa Receipt:</span>
                        <span className="font-mono text-gray-900">{notification.transactionRef}</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment reference */}
                <p className="text-xs text-gray-400 mt-4">
                  Reference: {paymentRef}
                </p>
              </div>
            </div>

            {/* Status Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-center">
              {(wsStatus === 'COMPLETED' || wsStatus === 'FAILED' || wsStatus === 'CANCELLED' || wsStatus === 'TIMEOUT') ? (
                <button
                  onClick={closePaymentStatus}
                  className={`px-6 py-2 rounded-lg text-white ${
                    wsStatus === 'COMPLETED' ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-700'
                  }`}
                >
                  {wsStatus === 'COMPLETED' ? 'Done' : 'Close'}
                </button>
              ) : (
                <button
                  onClick={closePaymentStatus}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
